import React, { useState, useEffect, useMemo } from 'react';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { Autocomplete, TextField, Box, Typography, useTheme, alpha , Dialog, DialogTitle, DialogContent, DialogActions, Button } from '@mui/material';
import { Server, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import dayjs from 'dayjs';
import * as XLSX from 'xlsx';
import styles from './IndentDemandReport.module.css';
import Header from '../../Header';
import { local_signage, local_url } from '../../Const';

export const TransferOut = () => {
  const theme = useTheme();
  
  // State for filters
  const [selectedArea, setSelectedArea] = useState('');
  const [selectedBranch, setSelectedBranch] = useState('');
  const [fromDate, setFromDate] = useState(dayjs().subtract(1, 'month')); // 1 month before today
  const [toDate, setToDate] = useState(dayjs()); // Today's date
  const [selectedStatus, setSelectedStatus] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showSessionDialog, setShowSessionDialog] = useState(false);

  // New state for segments, areas, branches, and branchType
  const [segments, setSegments] = useState([]);
  const [branchType, setBranchType] = useState('');
  const [areas, setAreas] = useState([]);
  const [branches, setBranches] = useState([]);
  const [selectedAreaObj, setSelectedAreaObj] = useState(null);
  const [selectedBranchObj, setSelectedBranchObj] = useState(null);
  
  // Loading states for dropdowns
  const [loadingAreas, setLoadingAreas] = useState(false);
  const [loadingBranches, setLoadingBranches] = useState(false);
  
  // Enhanced state for table with multi-column sorting
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(500);
  const [sortConfig, setSortConfig] = useState({ 
    key: 'date', 
    direction: 'desc',
    secondarySort: null // For secondary sorting
  });
  const [rawData, setRawData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);

  // Get user data from localStorage
  const userId = localStorage.getItem("userId");
  const authToken = localStorage.getItem("AuthorizationToken");

  // Enhanced sorting configuration with data types
  const sortableColumns = {
    'sno': { type: 'number', label: 'S.No' },
    'date': { type: 'date', label: 'Demand Date' },
    'area': { type: 'string', label: 'Area' },
    'fromLocation': { type: 'string', label: 'Dispatcher' },
    'toLocation': { type: 'string', label: 'Indenter' },
    'itemCode': { type: 'string', label: 'Item Code' },
    'itemName': { type: 'string', label: 'Item Name' },
    'demandQtyKg': { type: 'number', label: 'Demand Qty in Kg' },
    'demandQtyEA': { type: 'number', label: 'Demand Qty in EA' },
    'qtyTransferred': { type: 'number', label: 'Qty Transferred' },
    'qtyEATransferred': { type: 'number', label: 'Qty EA Transferred' },
    'transferInQtyKg': { type: 'number', label: 'Transfer In Qty KG' },
    'transferInQtyEA': { type: 'number', label: 'Transfer In Qty EA' },
    'uom': { type: 'string', label: 'UOM' },
    'itemGroup': { type: 'string', label: 'Item Group' },
    // 'dailyMovement': { type: 'number', label: '% of Daily Movement' },
    'demandDateTime': { type: 'datetime', label: 'Demand DateTime' },
    'transferOutDateTime': { type: 'datetime', label: 'Transfer Out DateTime' },
    'transferInDateTime': { type: 'datetime', label: 'Transfer In DateTime' }
  };

  // FIXED: Helper function to format date and time separately - treating as IST time
  const formatDateTime = (dateString) => {
    if (!dateString) return { date: '', time: '' };
    
    try {
      // Remove the 'Z' and treat as local IST time instead of UTC
      const localDateString = dateString.replace('Z', '');
      const date = new Date(localDateString);
      if (isNaN(date.getTime())) return { date: '', time: '' };
      
      // Format date as DD-MM-YYYY
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      const formattedDate = `${day}-${month}-${year}`;
      
      // Format time as HH:MM AM/PM
      let hours = date.getHours();
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const ampm = hours >= 12 ? 'PM' : 'AM';
      hours = hours % 12;
      hours = hours ? hours : 12; // 0 should be 12
      const formattedTime = `${String(hours).padStart(2, '0')}:${minutes} ${ampm}`;
      
      return { date: formattedDate, time: formattedTime };
    } catch (error) {
      console.error('Error formatting date:', dateString, error);
      return { date: '', time: '' };
    }
  };

  // FIXED: Helper function to get just the date part for display in table
  const formatDateOnly = (dateString) => {
    if (!dateString) return '';
    
    try {
      // Remove the 'Z' and treat as local IST time instead of UTC
      const localDateString = dateString.replace('Z', '');
      const date = new Date(localDateString);
      if (isNaN(date.getTime())) return '';
      
      // Format date as DD-MM-YYYY
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      
      return `${day}-${month}-${year}`;
    } catch (error) {
      console.error('Error formatting date only:', dateString, error);
      return '';
    }
  };

  // FIXED: Helper function for datetime display in table (combined date and time)
  const formatDateTimeDisplay = (dateString) => {
    if (!dateString) return '';
    
    try {
      // Remove the 'Z' and treat as local IST time instead of UTC
      const localDateString = dateString.replace('Z', '');
      const date = new Date(localDateString);
      if (isNaN(date.getTime())) return '';
      
      // Format date as DD-MM-YYYY
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      
      // Format time as HH:MM AM/PM
      let hours = date.getHours();
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const ampm = hours >= 12 ? 'PM' : 'AM';
      hours = hours % 12;
      hours = hours ? hours : 12; // 0 should be 12
      
      return `${day}-${month}-${year} ${String(hours).padStart(2, '0')}:${minutes} ${ampm}`;
    } catch (error) {
      console.error('Error formatting datetime display:', dateString, error);
      return '';
    }
  };

  // Enhanced sort function with proper data type handling
  const getSortValue = (item, key, dataType) => {
    let value = item[key];

    switch (dataType) {
      case 'number':
        return parseFloat(value) || 0;
      
      case 'date':
      case 'datetime':
        if (!value) return new Date(0); // Return epoch for empty dates
        
        // Handle different date formats
        let dateValue;
        if (typeof value === 'string') {
          const cleanValue = value.trim();
          
          // Handle DD/MM/YYYY format (e.g., "01/04/2025")
          if (cleanValue.includes('/')) {
            const parts = cleanValue.split('/');
            if (parts.length === 3) {
              const [day, month, year] = parts.map(p => parseInt(p, 10));
              if (!isNaN(day) && !isNaN(month) && !isNaN(year)) {
                dateValue = new Date(year, month - 1, day);
              }
            }
          } 
          // Handle DD-MM-YYYY format (e.g., "01-04-2025")
          else if (cleanValue.includes('-')) {
            const parts = cleanValue.split('-');
            if (parts.length === 3) {
              const [day, month, year] = parts.map(p => parseInt(p, 10));
              if (!isNaN(day) && !isNaN(month) && !isNaN(year)) {
                dateValue = new Date(year, month - 1, day);
              }
            }
          }
          // Handle YYYY-MM-DD ISO format or other ISO strings
          else if (cleanValue.length >= 8) {
            dateValue = new Date(cleanValue);
          }
          
          // If no valid date was created, try parsing as is
          if (!dateValue || isNaN(dateValue.getTime())) {
            dateValue = new Date(cleanValue);
          }
        } else {
          dateValue = new Date(value);
        }
        
        return isNaN(dateValue.getTime()) ? new Date(0) : dateValue;
      
      case 'string':
      default:
        return String(value || '').toLowerCase().trim();
    }
  };

  // Enhanced sorting function with multi-level sorting
  const handleSort = (key) => {
    const columnConfig = sortableColumns[key];
    if (!columnConfig) return;

    let direction = 'asc';
    let secondarySort = null;

    // Toggle direction if clicking the same column
    if (sortConfig.key === key) {
      if (sortConfig.direction === 'asc') {
        direction = 'desc';
      } else if (sortConfig.direction === 'desc') {
        // Third click: clear sorting
        setSortConfig({ key: null, direction: 'asc', secondarySort: null });
        return;
      }
    } else {
      // New column: start with ascending, keep previous sort as secondary
      if (sortConfig.key) {
        secondarySort = { key: sortConfig.key, direction: sortConfig.direction };
      }
    }

    setSortConfig({ key, direction, secondarySort });
  };

  // Clear all sorting
  const clearSort = () => {
    setSortConfig({ key: 'date', direction: 'desc', secondarySort: null });
  };

  // Enhanced sort icon component
  const SortIcon = ({ columnKey, currentSort }) => {
    if (currentSort.key === columnKey) {
      return currentSort.direction === 'asc' ? 
        <ArrowUp size={14} style={{ marginLeft: '4px', color: '#667eea' }} /> : 
        <ArrowDown size={14} style={{ marginLeft: '4px', color: '#667eea' }} />;
    }
    
    // Show secondary sort indicator
    if (currentSort.secondarySort && currentSort.secondarySort.key === columnKey) {
      return currentSort.secondarySort.direction === 'asc' ? 
        <ArrowUp size={12} style={{ marginLeft: '4px', color: '#94a3b8', opacity: 0.7 }} /> : 
        <ArrowDown size={12} style={{ marginLeft: '4px', color: '#94a3b8', opacity: 0.7 }} />;
    }
    
    return <ArrowUpDown size={12} style={{ marginLeft: '4px', color: '#cbd5e1', opacity: 0.5 }} />;
  };

  // Date picker styling
  const datePickerStyle = {
    '& .MuiOutlinedInput-root': {
      height: '35px',
      fontSize: '14px',
      backgroundColor: '#ffffff',
      '& fieldset': {
        borderColor: '#dee2e6',
      },
      '&:hover fieldset': {
        borderColor: '#adb5bd',
      },
      '&.Mui-focused fieldset': {
        borderColor: '#667eea',
      },
    },
    '& .MuiInputLabel-root': {
      fontSize: '14px',
      color: '#6c757d',
    },
  };

  const statusOptions = [
    { value: 'open', label: 'Open' },
    { value: 'closed', label: 'Closed' }
  ];

  // Session timeout handler
  const handleSessionTimeout = () => {
    // Clear all localStorage data
    localStorage.clear();
    
    // Show session timeout alert
    setShowSessionDialog(false);
    alert('Session Timeout! Please login again.');
    // Navigate to login page
    window.location.href = '/Login';
  };

  // Generic API call wrapper with 401 handling
  const apiCall = async (url, options = {}) => {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 60000); // 60 seconds

    try {
      const response = await fetch(url, {
        ...options,
        signal: controller.signal,
      });

      clearTimeout(timeout);

      if (response.status === 401 || response.status === 403) {
        setShowSessionDialog(true);
        throw new Error('Session expired');
      }

      return response;
    } catch (error) {
      clearTimeout(timeout);
      if (error.name === 'AbortError') {
        alert('Request timed out. Please try again.');
      }
      if (error.message === 'Session expired') {
        throw error;
      }
      console.error('API call error:', error);
      throw error;
    }
  };

  const SessionTimeoutDialog = () => (
    <Dialog
      open={showSessionDialog}
      onClose={() => {}} // Prevent closing by clicking outside
      disableEscapeKeyDown // Prevent closing with escape key
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle 
        sx={{ 
          backgroundColor: '#f44336', 
          color: 'white',
          textAlign: 'center',
          fontWeight: 'bold'
        }}
      >
        Session Timeout
      </DialogTitle>
      <DialogContent sx={{ textAlign: 'center', py: 3 }}>
        <Typography variant="h6" gutterBottom>
          Your session has expired
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Please login again to continue using the application.
        </Typography>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
        <Button
          onClick={handleSessionTimeout}
          variant="contained"
          color="primary"
          size="large"
          sx={{ minWidth: 120 }}
        >
          Login Again
        </Button>
      </DialogActions>
    </Dialog>
  );

  // Loader Component
  const FullPageLoader = () => (
    <Box
      sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'rgba(248, 250, 252, 0.85)',
        backdropFilter: 'blur(8px)',
        zIndex: 9999,
      }}
    >
      <Box
        sx={{
          width: 100,
          height: 100,
          position: 'relative',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          mb: 3,
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            width: '100%',
            height: '100%',
            borderRadius: '50%',
            border: '3px solid transparent',
            borderTopColor: theme.palette.primary.main,
            animation: 'spin 1s linear infinite',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            width: '80%',
            height: '80%',
            borderRadius: '50%',
            border: '3px solid transparent',
            borderTopColor: theme.palette.secondary?.main || '#1976d2',
            animation: 'spin 1.5s linear infinite',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            width: '60%',
            height: '60%',
            borderRadius: '50%',
            border: '3px solid transparent',
            borderTopColor: theme.palette.info?.main || '#0288d1',
            animation: 'spin 2s linear infinite',
          }}
        />

        <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'white',
          borderRadius: '50%',
          width: '40%',
          height: '40%',
          boxShadow: '0 4px 10px rgba(0, 0, 0, 0.1)',
        }}
        >
          <Server
            size={20}
            color={theme.palette.primary.main}
          />
        </Box>
      </Box>

      <Typography
        variant="h5"
        color="#004D00"
        mb={1}
        sx={{ fontWeight: 600 }}
      >
        Loading Transfer Data
      </Typography>

      <Typography
        variant="body2"
        color="text.secondary"
        align="center"
        sx={{
          maxWidth: '300px',
          animation: 'pulse 1.5s infinite ease-in-out',
        }}
      >
        Please wait while we load transfer out data...
      </Typography>

      <Box
        sx={{
          mt: 4,
          height: 4,
          width: 220,
          borderRadius: 2,
          overflow: 'hidden',
          backgroundColor: alpha(theme.palette.primary.main, 0.1),
        }}
      >
        <Box
          sx={{
            height: '100%',
            width: '100%',
            backgroundImage: `linear-gradient(90deg, 
              rgba(255,255,255,0), 
              #004D00, 
              rgba(255,255,255,0))`,
            backgroundSize: '1000px 100%',
            animation: 'shimmer 2s infinite',
          }}
        />
      </Box>
    </Box>
  );

  // Fetch Segments on Mount
  useEffect(() => {
    const fetchSegments = async () => {
      try {
        const response = await fetch(`${local_url}/api/BizSegmentbyParam/GetBizSegmentByUserId/${userId}`, {
          headers: {
            'Authorization': `Bearer ${authToken}`,
            'Content-Type': 'application/json',
          },
        });
        
        if (response.status === 401 || response.status === 403) {
          setShowSessionDialog(true);
          return;
        }
        
        if (response.ok) {
          const data = await response.json();
          setSegments(data.filter(segment => segment.deleted === "N"));
        }
      } catch (error) {
        console.error('Error fetching business segments:', error);
      }
    };

    if (authToken && userId) fetchSegments();
  }, [authToken, userId]);

  // Fetch Areas When BranchType Changes
  useEffect(() => {
    const fetchAreasBySegment = async () => {
      if (!branchType) return;
      
      setLoadingAreas(true);
      try {
        const response = await fetch(`${local_signage}/api/v1/sfareabysegment?SegmentCode=${branchType}`, {
          headers: {
            'Authorization': `Bearer ${authToken}`,
            'Content-Type': 'application/json',
          },
        });
        
        if (response.status === 401 || response.status === 403) {
          setShowSessionDialog(true);
          return;
        }
        
        if (response.ok) {
          const data = await response.json();
          // Add "ALL" option to areas
          const areasWithAll = [
            { SFAreaId: 'ALL', SFAreaName: 'ALL' },
            ...data // data from the segment API response
          ];
          setAreas(areasWithAll);
          setSelectedAreaObj(null);
        }
      } catch (error) {
        console.error('Error fetching areas by segment:', error);
        setAreas([]);
      } finally {
        setLoadingAreas(false);
      }
    };

    if (authToken && branchType) fetchAreasBySegment();
  }, [branchType, authToken]);

  // Fetch Branches When Area or BranchType Changes
  useEffect(() => {
    const fetchBranchList = async () => {
      if (!branchType || !selectedAreaObj) return;
      
      setLoadingBranches(true);
      try {
        // Use SFAreaId from the selected area object
        const areaParam = selectedAreaObj.SFAreaId || selectedAreaObj.sfAreaId || "ALL";
        const url = `${local_url}/api/Company/GetBranchList/${areaParam}/SEGMENT/${branchType}`;
        const response = await fetch(url, {
          headers: {
            'Authorization': `Bearer ${authToken}`,
            'Content-Type': 'application/json',
          },
        });
        
        if (response.status === 401 || response.status === 403) {
          setShowSessionDialog(true);
          return;
        }
        
        if (response.ok) {
          const data = await response.json();
          setBranches([{ branchID: 'ALL', branchName: 'ALL' }, ...data]);
          setSelectedBranchObj(null);
        }
      } catch (error) {
        console.error('Error fetching branch list:', error);
        setBranches([]);
      } finally {
        setLoadingBranches(false);
      }
    };

    if (authToken && branchType && selectedAreaObj) fetchBranchList();
  }, [branchType, selectedAreaObj, authToken]);

  const fetchTransferOutData = async () => {
    if (!selectedAreaObj) {
      alert('Please select an Area');
      return;
    }
    if (!fromDate || !toDate) {
      alert('Please select From Date and To Date');
      return;
    }

    setLoading(true);
    try {
      const startDate = fromDate.format('YYYYMMDD');
      const endDate = toDate.format('YYYYMMDD');
      const branchId = selectedBranchObj ? selectedBranchObj.branchID : 'ALL';
      
      // Use SFAreaId from the selected area object for the main API
      const areaId = selectedAreaObj.SFAreaId || selectedAreaObj.sfAreaId;
      const apiUrl = `${local_signage}/api/v1/reportjsontransferout?BizSegmentId=${branchType || 'ALL'}&AreaID=${areaId}&BranchID=${branchId}&ItemID=ALL&StartDate=${startDate}&EndDate=${endDate}`;
      
      const response = await apiCall(apiUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response && response.ok) {
        const responseData = await response.json();
        // Handle the new API response structure with TrOut wrapper
        const apiData = responseData.TrOut || responseData || [];
        const processedData = [];
        let rowIndex = 1;
        
        apiData.forEach((transferOut) => {
          // Get demand data - check if demand array exists and has items
          const demandItems = transferOut.demand && transferOut.demand[0] && transferOut.demand[0].demandLine 
            ? transferOut.demand[0].demandLine 
            : [];
          
          // Get transfer in (Gr) data - check if Gr array exists and has items
          const grItems = transferOut.Gr && transferOut.Gr[0] && transferOut.Gr[0].GRLine 
            ? transferOut.Gr[0].GRLine 
            : [];
          
          // If there are no demand items, create a single row with transfer out data
          if (demandItems.length === 0) {
            const demandDateTime = formatDateTime(transferOut.demand && transferOut.demand[0] ? transferOut.demand[0].IndentTime : transferOut.BusinessDate);
            const transferOutDateTime = formatDateTime(transferOut.TransferOutDate);
            const transferInDateTime = formatDateTime(transferOut.Gr && transferOut.Gr[0] ? transferOut.Gr[0].GRTime : '');
            
            // Find matching GR item for the main transfer item
            const matchingGrItem = grItems.find(gr => gr.ItemID === transferOut.ItemID) || {};
            
            processedData.push({
              id: rowIndex++,
              sno: processedData.length + 1,
              date: formatDateOnly(transferOut.demand && transferOut.demand[0] ? transferOut.demand[0].IndentDate : transferOut.BusinessDate),
              area: transferOut.AreaName || '',
              fromLocation: transferOut.BranchName || '',
              toLocation: transferOut.toBranchName || '',
              itemCode: transferOut.ItemID || '',
              itemName: transferOut.itemname || '',
              
              // Use main transfer out data as fallback
              demandQtyKg: transferOut.KG || transferOut.AltQty || 0,
              demandQtyEA: transferOut.EA || 0,
              qtyTransferred: transferOut.KG || transferOut.AltQty || 0,
              qtyEATransferred: transferOut.EA || 0,
              
              // NEW: Transfer In data from Gr array
              transferInQtyKg: matchingGrItem.GrKG || 0,
              transferInQtyEA: matchingGrItem.GrEA || 0,
              
              uom: (transferOut.KG > 0 || transferOut.AltQty > 0) ? 'KG' : 'EA',
              itemGroup: transferOut.ItemFamilyName || 'General',
              
              // Date/time fields
              demandDate: demandDateTime.date,
              demandTime: demandDateTime.time,
              indentNumber: transferOut.RefIndentID ? `${transferOut.ToBranchId}-${transferOut.RefIndentID}` : '',
              toNumber: transferOut.TrOutID ? `${transferOut.BranchId}-${transferOut.TrOutID}` : '',
              tiNumber: transferOut.RecGRNId ? `${transferOut.ToBranchId}-${transferOut.RecGRNId}` : '',
              transferOutDate: transferOutDateTime.date,
              transferOutTime: transferOutDateTime.time,
              transferInDate: transferInDateTime.date,
              transferInTime: transferInDateTime.time,
              
              // Legacy compatibility fields
              demandDateTime: formatDateTimeDisplay(transferOut.demand && transferOut.demand[0] ? transferOut.demand[0].IndentTime : transferOut.BusinessDate),
              transferOutDateTime: formatDateTimeDisplay(transferOut.TransferOutDate),
              transferInDateTime: formatDateTimeDisplay(transferOut.Gr && transferOut.Gr[0] ? transferOut.Gr[0].GRTime : ''),
              
              // Original API fields for reference
              branchId: transferOut.BranchId,
              trOutID: transferOut.TrOutID,
              transTypeID: transferOut.TransTypeID,
              businessDate: transferOut.BusinessDate,
              toBranchId: transferOut.ToBranchId,
              remarks: transferOut.Remarks,
              refIndentID: transferOut.RefIndentID,
              vehicleNumber: transferOut.VehicleNumber,
              driverName: transferOut.DriverName,
              lineNumber: transferOut.LineNumber,
              recGRNId: transferOut.RecGRNId,
              altQty: transferOut.AltQty
            });
          } else {
            // Process each demand item separately to create individual rows
            demandItems.forEach((demandItem) => {
              const demandDateTime = formatDateTime(transferOut.demand[0].IndentTime);
              const transferOutDateTime = formatDateTime(transferOut.TransferOutDate);
              const transferInDateTime = formatDateTime(transferOut.Gr && transferOut.Gr[0] ? transferOut.Gr[0].GRTime : '');
              
              // Find matching GR item for this specific demand item
              const matchingGrItem = grItems.find(gr => gr.ItemID === demandItem.ItemID) || {};
              
              processedData.push({
                id: rowIndex++,
                sno: processedData.length + 1,
                date: formatDateOnly(transferOut.demand[0].IndentDate),
                area: transferOut.AreaName || '',
                fromLocation: transferOut.BranchName || '',
                toLocation: transferOut.toBranchName || '',
                itemCode: demandItem.ItemID || '',
                itemName: demandItem.ItemName || '',
                
                // Use demand item data
                demandQtyKg: demandItem.demandKG || 0,
                demandQtyEA: demandItem.demandEA || 0,
                
                // Use transfer out data for this item (if it matches the current demand item)
                qtyTransferred: transferOut.ItemID === demandItem.ItemID ? (transferOut.KG || transferOut.AltQty || 0) : 0,
                qtyEATransferred: transferOut.ItemID === demandItem.ItemID ? (transferOut.EA || 0) : 0,
                
                // NEW: Transfer In data from Gr array
                transferInQtyKg: matchingGrItem.GrKG || 0,
                transferInQtyEA: matchingGrItem.GrEA || 0,
                
                uom: (demandItem.demandKG > 0) ? 'KG' : 'EA',
                itemGroup: transferOut.ItemFamilyName || 'General',
                
                // Date/time fields
                demandDate: demandDateTime.date,
                demandTime: demandDateTime.time,
                indentNumber: transferOut.RefIndentID ? `${transferOut.ToBranchId}-${transferOut.RefIndentID}` : '',
                toNumber: transferOut.TrOutID ? `${transferOut.BranchId}-${transferOut.TrOutID}` : '',
                tiNumber: transferOut.RecGRNId ? `${transferOut.ToBranchId}-${transferOut.RecGRNId}` : '',
                transferOutDate: transferOutDateTime.date,
                transferOutTime: transferOutDateTime.time,
                transferInDate: transferInDateTime.date,
                transferInTime: transferInDateTime.time,
                
                // Legacy compatibility fields
                demandDateTime: formatDateTimeDisplay(transferOut.demand[0].IndentTime),
                transferOutDateTime: formatDateTimeDisplay(transferOut.TransferOutDate),
                transferInDateTime: formatDateTimeDisplay(transferOut.Gr && transferOut.Gr[0] ? transferOut.Gr[0].GRTime : ''),
                
                // Original API fields for reference
                branchId: transferOut.BranchId,
                trOutID: transferOut.TrOutID,
                transTypeID: transferOut.TransTypeID,
                businessDate: transferOut.BusinessDate,
                toBranchId: transferOut.ToBranchId,
                remarks: transferOut.Remarks,
                refIndentID: transferOut.RefIndentID,
                vehicleNumber: transferOut.VehicleNumber,
                driverName: transferOut.DriverName,
                lineNumber: transferOut.LineNumber,
                recGRNId: transferOut.RecGRNId,
                altQty: transferOut.AltQty
              });
            });
          }
        });
        
        setRawData(processedData);
        setFilteredData(processedData);
      } else if (response) {
        console.error('API Error:', response.statusText);
        alert('Failed to fetch data');
      }
    } catch (error) {
      console.error('Error fetching transfer out data:', error);
      alert('Error fetching data');
    } finally {
      setLoading(false);
    }
  };

  // Initialize data
  useEffect(() => {
    // Remove mock data generation since we'll use API data
    setRawData([]);
    setFilteredData([]);
  }, []);

  // Apply filters - Updated to call API with sorting preservation
  const applyFilters = () => {
    if (!selectedAreaObj) {
      alert('Please select an Area');
      return;
    }
    if (!fromDate || !toDate) {
      alert('Please select From Date and To Date');
      return;
    }
    
    // Validate dates
    if (fromDate.isAfter(dayjs())) {
      alert('From Date cannot be greater than today');
      return;
    }
    
    if (toDate.isAfter(dayjs())) {
      alert('To Date cannot be greater than today');
      return;
    }
    
    if (fromDate.isAfter(toDate)) {
      alert('From Date cannot be greater than To Date');
      return;
    }
    
    // Preserve current sorting when fetching new data (default to date desc if no sorting)
    const currentSortConfig = sortConfig.key ? { ...sortConfig } : { key: 'date', direction: 'desc', secondarySort: null };
    
    fetchTransferOutData().then(() => {
      // Restore sorting after data is loaded
      setSortConfig(currentSortConfig);
    });
  };

  // Clear filters
  const clearFilters = () => {
    setSelectedArea('');
    setSelectedBranch('');
    setBranchType('');
    setSelectedAreaObj(null);
    setSelectedBranchObj(null);
    setFromDate(dayjs().subtract(1, 'month')); // Reset to 1 month before today
    setToDate(dayjs()); // Reset to today
    setSelectedStatus('');
    setSearchTerm('');
    setRawData([]);
    setFilteredData([]);
    setCurrentPage(1);
    setSortConfig({ key: 'date', direction: 'desc', secondarySort: null }); // Reset to default sorting
  };

  // Helper to calculate TAT between two date/time pairs (returns "Xh Ym")
  const getTAT = (date1, time1, date2, time2) => {
    if (!date1 || !time1 || !date2 || !time2) return '-';
    
    // Enhanced date/time parser that handles both DD-MM-YYYY and DD/MM/YYYY
    const parseDT = (d, t) => { 
      let day, month, year;
      
      // Parse date - handle both DD-MM-YYYY and DD/MM/YYYY formats
      if (d.includes('-')) {
        [day, month, year] = d.split('-').map(Number);
      } else if (d.includes('/')) {
        [day, month, year] = d.split('/').map(Number);
      } else {
        return new Date(0); // Invalid format
      }
      
      // Parse time - handle "HH:MM AM/PM" format
      if (!t || !t.includes(':')) return new Date(0);
      
      let [hm, ampm] = t.split(' ');
      let [h, m] = hm.split(':').map(Number);
      
      // Handle AM/PM conversion
      if (ampm) {
        if (ampm.toUpperCase() === 'PM' && h !== 12) h += 12;
        if (ampm.toUpperCase() === 'AM' && h === 12) h = 0;
      }
      
      // Validate parsed values
      if (isNaN(day) || isNaN(month) || isNaN(year) || isNaN(h) || isNaN(m)) {
        return new Date(0);
      }
      
      return new Date(year, month - 1, day, h, m);
    };
    
    const dt1 = parseDT(date1, time1);
    const dt2 = parseDT(date2, time2);
    
    if (isNaN(dt1.getTime()) || isNaN(dt2.getTime()) || dt1.getTime() === 0 || dt2.getTime() === 0) {
      return '-';
    }
    
    let diff = (dt2 - dt1) / 1000; // seconds
    if (diff < 0) return '-';
    
    const h = Math.floor(diff / 3600);
    const m = Math.floor((diff % 3600) / 60);
    return `${h}h ${m}m`;
  };

  // FIXED: Export function with corrected date/time fields
  const exportToXLSX = () => {
    const exportData = filteredAndSearchedData.map(row => ({
      'Demand Date': row.demandDate,
      'Demand Time': row.demandTime,
      'TAT (Demand to TransferOut)': getTAT(row.demandDate, row.demandTime, row.transferOutDate, row.transferOutTime),
      'TransferOut Date': row.transferOutDate,
      'TransferOut Time': row.transferOutTime,
      'TAT (TransferOut to TransferIn)': getTAT(row.transferOutDate, row.transferOutTime, row.transferInDate, row.transferInTime),
      'TransferIn Date': row.transferInDate,
      'TransferIn Time': row.transferInTime,
      'Overall TAT (Demand to TransferIn)': getTAT(row.demandDate, row.demandTime, row.transferInDate, row.transferInTime),
      'Indent Number': row.indentNumber,
      'TO Number': row.toNumber,
      'TI Number': row.tiNumber,
      'AREA': row.area,
      'Indenter': row.toLocation,
      // 'ToBranchId': row.toBranchId,
      'Dispatcher': row.fromLocation,
      'Item Code': row.itemCode,
      'Item Name': row.itemName,
      'Demand Qty in Kg': row.demandQtyKg,
      'Demand Qty in EA': row.demandQtyEA,
      'Qty Transferred': row.qtyTransferred,
      'Qty EA Transferred': row.qtyEATransferred,
      'Transfer In Qty KG': row.transferInQtyKg,
      'Transfer In Qty EA': row.transferInQtyEA,
      'UOM': row.uom,
      'Item Group': row.itemGroup,
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Transfer Out Report');
    XLSX.writeFile(wb, 'transfer_out_report.xlsx');
  };

  // Enhanced filtering and sorting with proper data type handling
  const filteredAndSearchedData = useMemo(() => {
    let data = filteredData;
    
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase().trim();
      
      data = data.filter(item => {
        // Search in Item Name specifically
        return item.itemName?.toString().toLowerCase().includes(searchLower) || false;
      });
    }
    
    if (!sortConfig.key) return data;
    
    return [...data].sort((a, b) => {
      const columnConfig = sortableColumns[sortConfig.key];
      if (!columnConfig) return 0;

      // Primary sort
      const aValue = getSortValue(a, sortConfig.key, columnConfig.type);
      const bValue = getSortValue(b, sortConfig.key, columnConfig.type);
      
      let primaryCompare = 0;
      
      if (columnConfig.type === 'number') {
        primaryCompare = aValue - bValue;
      } else if (columnConfig.type === 'date' || columnConfig.type === 'datetime') {
        // Ensure both values are valid dates
        const aTime = aValue.getTime();
        const bTime = bValue.getTime();
        primaryCompare = aTime - bTime;
      } else {
        // String comparison
        if (aValue < bValue) primaryCompare = -1;
        else if (aValue > bValue) primaryCompare = 1;
        else primaryCompare = 0;
      }
      
      // Apply primary sort direction
      primaryCompare = sortConfig.direction === 'desc' ? -primaryCompare : primaryCompare;
      
      // If primary sort values are equal, apply secondary sort
      if (primaryCompare === 0 && sortConfig.secondarySort) {
        const secondaryColumnConfig = sortableColumns[sortConfig.secondarySort.key];
        if (secondaryColumnConfig) {
          const aSecondaryValue = getSortValue(a, sortConfig.secondarySort.key, secondaryColumnConfig.type);
          const bSecondaryValue = getSortValue(b, sortConfig.secondarySort.key, secondaryColumnConfig.type);
          
          let secondaryCompare = 0;
          
          if (secondaryColumnConfig.type === 'number') {
            secondaryCompare = aSecondaryValue - bSecondaryValue;
          } else if (secondaryColumnConfig.type === 'date' || secondaryColumnConfig.type === 'datetime') {
            // Ensure both secondary values are valid dates
            const aSecondaryTime = aSecondaryValue.getTime();
            const bSecondaryTime = bSecondaryValue.getTime();
            secondaryCompare = aSecondaryTime - bSecondaryTime;
          } else {
            // String comparison
            if (aSecondaryValue < bSecondaryValue) secondaryCompare = -1;
            else if (aSecondaryValue > bSecondaryValue) secondaryCompare = 1;
            else secondaryCompare = 0;
          }
          
          // Apply secondary sort direction
          return sortConfig.secondarySort.direction === 'desc' ? -secondaryCompare : secondaryCompare;
        }
      }
      
      return primaryCompare;
    }).map((item, index) => ({
      ...item,
      sno: index + 1 // Preserve sequential S.No regardless of sorting
    }));
  }, [filteredData, sortConfig, searchTerm]);

  // Auto-redirect to first page when search results change
  useEffect(() => {
    // Reset to first page when search term changes or when filtered results change
    if (searchTerm || filteredAndSearchedData.length > 0) {
      const maxPossiblePages = Math.ceil(filteredAndSearchedData.length / rowsPerPage);
      if (currentPage > maxPossiblePages && maxPossiblePages > 0) {
        setCurrentPage(1);
      }
    }
  }, [searchTerm, filteredAndSearchedData.length, rowsPerPage, currentPage]);

  // Reset to first page when search term changes
  useEffect(() => {
    if (searchTerm) {
      setCurrentPage(1);
    }
  }, [searchTerm]);

  // Pagination
  const totalPages = Math.ceil(filteredAndSearchedData.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const paginatedData = filteredAndSearchedData.slice(startIndex, startIndex + rowsPerPage);

  return (
    <>
      <Header/>
      <div className={styles.indent_dashboard}>
        {/* Filters Section */}
        <div className={styles.indent_filters_section}>
          <div className={styles.indent_filters_grid}>
            {/* Segment Dropdown */}
            <div className={styles.indent_filter_group}>
              <select
                value={branchType}
                onChange={e => setBranchType(e.target.value)}
                className={styles.indent_filter_select}
                style={{
                  height: '35px',
                  fontSize: '14px',
                  backgroundColor: '#ffffff',
                  border: '1px solid #dee2e6',
                  borderRadius: '4px',
                  padding: '0 12px',
                  color: '#495057',
                  minWidth: '200px',
                  outline: 'none'
                }}
                onFocus={(e) => e.target.style.borderColor = '#667eea'}
                onBlur={(e) => e.target.style.borderColor = '#dee2e6'}
              >
                <option value="">Select Segment</option>
                {segments.map(segment => (
                  <option key={segment.businessSegmentCode} value={segment.businessSegmentCode}>
                    {segment.businessSegmentName}
                  </option>
                ))}
              </select>
            </div>

            {/* Area Dropdown */}
            <div className={styles.indent_filter_group}>
              <Autocomplete
                options={areas}
                getOptionLabel={(option) => option.SFAreaName || option.sfAreaName || ''}
                value={selectedAreaObj}
                onChange={(event, newValue) => {
                  setSelectedAreaObj(newValue);
                }}
                disabled={!branchType || loadingAreas}
                loading={loadingAreas}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={loadingAreas ? "Loading Areas..." : "Select Area"}
                    variant="outlined"
                    size="small"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        height: '35px',
                        fontSize: '14px',
                        backgroundColor: '#ffffff',
                      }
                    }}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingAreas ? <Box sx={{ display: 'flex', alignItems: 'center', mr: 1 }}>
                            <Box
                              sx={{
                                width: 16,
                                height: 16,
                                border: '2px solid #e0e0e0',
                                borderTop: '2px solid #1976d2',
                                borderRadius: '50%',
                                animation: 'spin 1s linear infinite'
                              }}
                            />
                          </Box> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                sx={{ minWidth: 200 }}
              />
            </div>

            {/* Branch Dropdown */}
            <div className={styles.indent_filter_group}>
              <Autocomplete
                options={branches}
                getOptionLabel={(option) => option.branchName || ''}
                value={selectedBranchObj}
                onChange={(event, newValue) => {
                  setSelectedBranchObj(newValue);
                }}
                disabled={!selectedAreaObj || loadingBranches}
                loading={loadingBranches}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={loadingBranches ? "Loading Branches..." : "Select Branch"}
                    variant="outlined"
                    size="small"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        height: '35px',
                        fontSize: '14px',
                        backgroundColor: '#ffffff',
                      }
                    }}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingBranches ? <Box sx={{ display: 'flex', alignItems: 'center', mr: 1 }}>
                            <Box
                              sx={{
                                width: 16,
                                height: 16,
                                border: '2px solid #e0e0e0',
                                borderTop: '2px solid #1976d2',
                                borderRadius: '50%',
                                animation: 'spin 1s linear infinite'
                              }}
                            />
                          </Box> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                sx={{ minWidth: 200 }}
              />
            </div>

            <div className={styles.indent_filter_group}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="From Date"
                  value={fromDate}
                  onChange={(newValue) => setFromDate(newValue)}
                  format="DD-MM-YYYY"
                  maxDate={dayjs()} // Disable dates after today
                  sx={datePickerStyle}
                  slotProps={{
                    textField: {
                      size: "small",
                      variant: "outlined"
                    }
                  }}
                />
              </LocalizationProvider>
            </div>

            <div className={styles.indent_filter_group}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="To Date"
                  value={toDate}
                  onChange={(newValue) => setToDate(newValue)}
                  format="DD-MM-YYYY"
                  maxDate={dayjs()} // Disable dates after today
                  minDate={fromDate} // To Date should not be less than From Date
                  sx={datePickerStyle}
                  slotProps={{
                    textField: {
                      size: "small",
                      variant: "outlined"
                    }
                  }}
                />
              </LocalizationProvider>
            </div>

            <div className={styles.indent_filter_group}>
              <select 
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
                className={styles.indent_filter_select}
              >
                <option value="">Select Status</option>
                {statusOptions.map(status => (
                  <option key={status.value} value={status.value}>{status.label}</option>
                ))}
              </select>
            </div>
          </div>

          <div className={styles.indent_filter_actions}>
            <button onClick={applyFilters} className={`${styles.indent_btn} ${styles.indent_btn_primary}`} disabled={loading}>
              {loading ? 'Loading...' : 'Apply'}
            </button>
            <button onClick={clearFilters} className={`${styles.indent_btn} ${styles.indent_btn_secondary}`}>
              Clear
            </button>
            <button onClick={exportToXLSX} className={`${styles.indent_btn} ${styles.indent_btn_success}`}>
              Export XLSX
            </button>
            {/* Enhanced sorting controls */}
            {sortConfig.key && sortConfig.key !== 'date' && (
              <button 
                onClick={clearSort} 
                className={`${styles.indent_btn} ${styles.indent_btn_warning}`}
                title="Reset to default sorting (Demand Date - Latest First)"
              >
                Reset Sort
              </button>
            )}
            <div className={styles.indent_search_group}>
              <input
                type="text"
                placeholder="Search by Item Name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={styles.indent_search_input}
              />
            </div>
          </div>
        </div>

        {/* Enhanced Results Summary with sorting info */}
        <div className={styles.indent_results_summary}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
            <span>Showing {paginatedData.length} of {filteredAndSearchedData.length} records</span>
            {sortConfig.key && (
              <span style={{ 
                fontSize: '12px', 
                color: '#667eea', 
                backgroundColor: '#f1f5f9', 
                padding: '2px 8px', 
                borderRadius: '12px',
                display: 'flex',
                alignItems: 'center',
                gap: '4px'
              }}>
                Sorted by: {sortableColumns[sortConfig.key]?.label} 
                ({sortConfig.direction === 'asc' ? 'ascending' : 'descending'})
                {sortConfig.secondarySort && (
                  <span style={{ opacity: 0.7 }}>
                    , then {sortableColumns[sortConfig.secondarySort.key]?.label}
                  </span>
                )}
              </span>
            )}
          </div>
          <div className={styles.indent_rows_per_page}>
            <label>Rows:</label>
            <select 
              value={rowsPerPage}
              onChange={(e) => {
                setRowsPerPage(Number(e.target.value));
                setCurrentPage(1);
              }}
              className={styles.indent_rows_select}
            >
              <option value={100}>100</option>
              <option value={200}>200</option>
              <option value={500}>500</option>
            </select>
          </div>
        </div>

        {/* Data Table Section with Enhanced Sorting */}
        <div className={styles.indent_table_section}>
          {loading ? (
            <FullPageLoader />
          ) : filteredAndSearchedData.length === 0 ? (
            <div className={styles.indent_no_data}>
              <p>No data found. Please select a Segment and Area, then click Apply to fetch data.</p>
            </div>
          ) : (
            <div className={styles.indent_table_container}>
              <table className={styles.indent_data_table}>
                <thead>
                  <tr>
                    <th 
                      onClick={() => handleSort('sno')} 
                      className={styles.indent_sortable}
                      style={{ cursor: 'pointer', userSelect: 'none' }}
                      title="Click to sort by S.No, click again to reverse, third click to clear"
                    >
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        S.No
                        <SortIcon columnKey="sno" currentSort={sortConfig} />
                      </div>
                    </th>
                    <th 
                      onClick={() => handleSort('date')} 
                      className={styles.indent_sortable}
                      style={{ cursor: 'pointer', userSelect: 'none' }}
                      title="Click to sort by Demand Date, click again to reverse, third click to clear"
                    >
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        Demand Date
                        <SortIcon columnKey="date" currentSort={sortConfig} />
                      </div>
                    </th>
                    <th 
                      onClick={() => handleSort('area')} 
                      className={styles.indent_sortable}
                      style={{ cursor: 'pointer', userSelect: 'none' }}
                      title="Click to sort by Area, click again to reverse, third click to clear"
                    >
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        Area
                        <SortIcon columnKey="area" currentSort={sortConfig} />
                      </div>
                    </th>
                    <th 
                      onClick={() => handleSort('fromLocation')} 
                      className={styles.indent_sortable}
                      style={{ cursor: 'pointer', userSelect: 'none' }}
                      title="Click to sort by Dispatcher, click again to reverse, third click to clear"
                    >
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        Dispatcher
                        <SortIcon columnKey="fromLocation" currentSort={sortConfig} />
                      </div>
                    </th>
                    <th 
                      onClick={() => handleSort('toLocation')} 
                      className={styles.indent_sortable}
                      style={{ cursor: 'pointer', userSelect: 'none' }}
                      title="Click to sort by Indenter, click again to reverse, third click to clear"
                    >
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        Indenter
                        <SortIcon columnKey="toLocation" currentSort={sortConfig} />
                      </div>
                    </th>
                    <th 
                      onClick={() => handleSort('itemCode')} 
                      className={styles.indent_sortable}
                      style={{ cursor: 'pointer', userSelect: 'none' }}
                      title="Click to sort by Item Code, click again to reverse, third click to clear"
                    >
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        Item Code
                        <SortIcon columnKey="itemCode" currentSort={sortConfig} />
                      </div>
                    </th>
                    <th 
                      onClick={() => handleSort('itemName')} 
                      className={styles.indent_sortable}
                      style={{ cursor: 'pointer', userSelect: 'none' }}
                      title="Click to sort by Item Name, click again to reverse, third click to clear"
                    >
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        Item Name
                        <SortIcon columnKey="itemName" currentSort={sortConfig} />
                      </div>
                    </th>
                    <th 
                      onClick={() => handleSort('demandQtyKg')} 
                      className={styles.indent_sortable}
                      style={{ cursor: 'pointer', userSelect: 'none' }}
                      title="Click to sort by Demand Qty in Kg, click again to reverse, third click to clear"
                    >
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        Demand Qty in Kg
                        <SortIcon columnKey="demandQtyKg" currentSort={sortConfig} />
                      </div>
                    </th>
                    <th 
                      onClick={() => handleSort('demandQtyEA')} 
                      className={styles.indent_sortable}
                      style={{ cursor: 'pointer', userSelect: 'none' }}
                      title="Click to sort by Demand Qty in EA, click again to reverse, third click to clear"
                    >
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        Demand Qty in EA
                        <SortIcon columnKey="demandQtyEA" currentSort={sortConfig} />
                      </div>
                    </th>
                    <th 
                      onClick={() => handleSort('qtyTransferred')} 
                      className={styles.indent_sortable}
                      style={{ cursor: 'pointer', userSelect: 'none' }}
                      title="Click to sort by Qty Transferred, click again to reverse, third click to clear"
                    >
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        Qty Transferred
                        <SortIcon columnKey="qtyTransferred" currentSort={sortConfig} />
                      </div>
                    </th>
                    <th 
                      onClick={() => handleSort('qtyEATransferred')} 
                      className={styles.indent_sortable}
                      style={{ cursor: 'pointer', userSelect: 'none' }}
                      title="Click to sort by Qty EA Transferred, click again to reverse, third click to clear"
                    >
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        Qty EA Transferred
                        <SortIcon columnKey="qtyEATransferred" currentSort={sortConfig} />
                      </div>
                    </th>
                    <th 
                      onClick={() => handleSort('transferInQtyKg')} 
                      className={styles.indent_sortable}
                      style={{ cursor: 'pointer', userSelect: 'none' }}
                      title="Click to sort by Transfer In Qty KG, click again to reverse, third click to clear"
                    >
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        Transfer In Qty KG
                        <SortIcon columnKey="transferInQtyKg" currentSort={sortConfig} />
                      </div>
                    </th>
                    <th 
                      onClick={() => handleSort('transferInQtyEA')} 
                      className={styles.indent_sortable}
                      style={{ cursor: 'pointer', userSelect: 'none' }}
                      title="Click to sort by Transfer In Qty EA, click again to reverse, third click to clear"
                    >
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        Transfer In Qty EA
                        <SortIcon columnKey="transferInQtyEA" currentSort={sortConfig} />
                      </div>
                    </th>
                    <th 
                      onClick={() => handleSort('uom')} 
                      className={styles.indent_sortable}
                      style={{ cursor: 'pointer', userSelect: 'none' }}
                      title="Click to sort by UOM, click again to reverse, third click to clear"
                    >
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        UOM
                        <SortIcon columnKey="uom" currentSort={sortConfig} />
                      </div>
                    </th>
                    <th 
                      onClick={() => handleSort('itemGroup')} 
                      className={styles.indent_sortable}
                      style={{ cursor: 'pointer', userSelect: 'none' }}
                      title="Click to sort by Item Group, click again to reverse, third click to clear"
                    >
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        Item Group
                        <SortIcon columnKey="itemGroup" currentSort={sortConfig} />
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedData.map((row) => (
                    <tr key={row.id} style={{ 
                      backgroundColor: row.sno % 2 === 0 ? '#f8f9fa' : 'white',
                      transition: 'background-color 0.2s ease'
                    }}>
                      <td style={{ fontWeight: '500', color: '#495057' }}>{row.sno}</td>
                      <td style={{ fontSize: '13px' }}>{row.date}</td>
                      <td className={styles.indent_capitalize} style={{ fontWeight: '500' }}>{row.area}</td>
                      <td style={{ fontSize: '13px' }}>{row.fromLocation}</td>
                      <td style={{ fontSize: '13px' }}>{row.toLocation}</td>
                      <td className={styles.indent_ticket_id} style={{ fontFamily: 'monospace', fontSize: '12px' }}>{row.itemCode}</td>
                      <td style={{ fontSize: '13px', maxWidth: '260px', wordWrap: 'break-word' }}>{row.itemName}</td>
                      <td className={styles.indent_amount} style={{ fontWeight: '500', color: row.demandQtyKg > 0 ? '#28a745' : '#6c757d' }}>
                        {row.demandQtyKg ? row.demandQtyKg.toLocaleString() : '0'}
                      </td>
                      <td className={styles.indent_amount} style={{ fontWeight: '500', color: row.demandQtyEA > 0 ? '#28a745' : '#6c757d' }}>
                        {row.demandQtyEA ? row.demandQtyEA.toLocaleString() : '0'}
                      </td>
                      <td className={styles.indent_amount} style={{ fontWeight: '500', color: row.qtyTransferred > 0 ? '#007bff' : '#6c757d' }}>
                        {row.qtyTransferred ? row.qtyTransferred.toLocaleString() : '0'}
                      </td>
                      <td className={styles.indent_amount} style={{ fontWeight: '500', color: row.qtyEATransferred > 0 ? '#007bff' : '#6c757d' }}>
                        {row.qtyEATransferred ? row.qtyEATransferred.toLocaleString() : '0'}
                      </td>
                      <td className={styles.indent_amount} style={{ fontWeight: '500', color: row.transferInQtyKg > 0 ? '#28a745' : '#6c757d' }}>
                        {row.transferInQtyKg ? row.transferInQtyKg.toLocaleString() : '0'}
                      </td>
                      <td className={styles.indent_amount} style={{ fontWeight: '500', color: row.transferInQtyEA > 0 ? '#28a745' : '#6c757d' }}>
                        {row.transferInQtyEA ? row.transferInQtyEA.toLocaleString() : '0'}
                      </td>
                      <td style={{ 
                        fontSize: '12px', 
                        fontWeight: '600',
                        color: row.uom === 'KG' ? '#dc3545' : '#17a2b8',
                        backgroundColor: row.uom === 'KG' ? '#ffeaea' : '#e6f7ff',
                        textAlign: 'center',
                        borderRadius: '4px',
                        padding: '2px 6px'
                      }}>
                        {row.uom}
                      </td>
                      <td style={{ fontSize: '13px' }}>{row.itemGroup}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Enhanced Pagination with First and Last options */}
        {filteredAndSearchedData.length > 0 && !loading && (
          <div className={styles.indent_pagination}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              {/* First button - always visible when there are multiple pages */}
              {totalPages > 1 && (
                <button
                  onClick={() => setCurrentPage(1)}
                  disabled={currentPage === 1}
                  className={`${styles.indent_btn} ${styles.indent_btn_pagination}`}
                  title="Go to first page"
                  style={{ 
                    opacity: currentPage === 1 ? 0.5 : 1,
                    cursor: currentPage === 1 ? 'not-allowed' : 'pointer'
                  }}
                >
                  First
                </button>
              )}
              
              <button 
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className={`${styles.indent_btn} ${styles.indent_btn_pagination}`}
                style={{ 
                  opacity: currentPage === 1 ? 0.5 : 1,
                  cursor: currentPage === 1 ? 'not-allowed' : 'pointer'
                }}
              >
                Prev
              </button>
              
              <div className={styles.indent_page_numbers}>
                {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                  let page;
                  if (totalPages <= 5) {
                    page = i + 1;
                  } else if (currentPage <= 3) {
                    page = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    page = totalPages - 4 + i;
                  } else {
                    page = currentPage - 2 + i;
                  }
                  return (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={`${styles.indent_btn} ${styles.indent_btn_pagination} ${currentPage === page ? styles.indent_active : ''}`}
                    >
                      {page}
                    </button>
                  );
                })}
              </div>
              
              <button 
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                className={`${styles.indent_btn} ${styles.indent_btn_pagination}`}
                style={{ 
                  opacity: currentPage === totalPages ? 0.5 : 1,
                  cursor: currentPage === totalPages ? 'not-allowed' : 'pointer'
                }}
              >
                Next
              </button>
              
              {/* Last button - always visible when there are multiple pages */}
              {totalPages > 1 && (
                <button
                  onClick={() => setCurrentPage(totalPages)}
                  disabled={currentPage === totalPages}
                  className={`${styles.indent_btn} ${styles.indent_btn_pagination}`}
                  title="Go to last page"
                  style={{ 
                    opacity: currentPage === totalPages ? 0.5 : 1,
                    cursor: currentPage === totalPages ? 'not-allowed' : 'pointer'
                  }}
                >
                  Last
                </button>
              )}
            </div>
            
            {/* Page information */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <span style={{ fontSize: '14px', color: '#6c757d', whiteSpace: 'nowrap' }}>
                Page {currentPage} of {totalPages}
              </span>
            </div>
          </div>
        )}

        {/* Session Timeout Dialog */}
        <SessionTimeoutDialog />
      </div>

      {/* Add CSS animations for sorting */}
      <style jsx>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.7; }
        }
        
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        
        .indent_data_table th:hover {
          background-color: #e3f2fd !important;
          transition: background-color 0.2s ease;
        }
        
        .indent_data_table tbody tr:hover {
          background-color: #f0f8ff !important;
          transform: translateY(-1px);
          box-shadow: 0 2px 4px rgba(0,0,0,0.1);
          transition: all 0.2s ease;
        }
      `}</style>
    </>
  );
};

export default TransferOut;